import React, { useMemo, useState } from 'react';
import * as turf from '@turf/turf';
import { FaChartBar, FaPlaneArrival, FaTimes } from 'react-icons/fa';

const EFRO_COORDS = [25.8476, 66.5608];

const TMA_ENTRY_POINTS = [
  { name: 'NEMGU' },
  { name: 'NEPIX' },
  { name: 'OSLIT' },
];

const ARRIVAL_ROUTES = [
  { name: 'OSS' },
  { name: 'OSLAV' },
  { name: 'SUN' },
  { name: 'DIVEG' },
  { name: 'OTVEG' },
  { name: 'TEVRU' },
];

const Stats = ({ trafficData = [] }) => {
  const [isVisible, setIsVisible] = useState(false);
  const check = true;

  const toggleRoutesMenu = () => setIsVisible(!isVisible);

  // Compute all stats from trafficData
  const { tmaTotals, tmaHourly, arrivalRouteTotals, topDepartures } =
    useMemo(() => {
      // Filter to only those with flight_plan and arrival EFRO
      const filtered = trafficData.filter(
        (p) =>
          p &&
          p.flight_plan &&
          typeof p.flight_plan.arrival === 'string' &&
          p.flight_plan.arrival.toUpperCase() === 'EFRO',
      );

      // Helper to safely uppercase route string
      const routeString = (p) =>
        p.flight_plan && p.flight_plan.route
          ? String(p.flight_plan.route).toUpperCase()
          : '';

      // 1) TMA totals (total occurrences in route)
      const tmaTotalsMap = {};
      TMA_ENTRY_POINTS.forEach((tp) => (tmaTotalsMap[tp.name] = 0));

      // 2) Arrival route totals
      const arrivalRouteTotalsMap = {};
      ARRIVAL_ROUTES.forEach((ar) => (arrivalRouteTotalsMap[ar.name] = 0));

      // 3) Departure counts
      const depCounts = {};

      // Populate totals
      filtered.forEach((p) => {
        const r = routeString(p);

        // Count tma entry points if included in route
        TMA_ENTRY_POINTS.forEach((tp) => {
          if (r.includes(tp.name)) tmaTotalsMap[tp.name] += 1;
        });

        // Count arrival routes if included in route
        ARRIVAL_ROUTES.forEach((ar) => {
          if (r.includes(ar.name)) arrivalRouteTotalsMap[ar.name] += 1;
        });

        // Count departures
        const dep =
          p.flight_plan && p.flight_plan.departure
            ? String(p.flight_plan.departure).toUpperCase()
            : null;
        if (dep) {
          depCounts[dep] = (depCounts[dep] || 0) + 1;
        }
      });

      const tmaHourlyMap = {};
      TMA_ENTRY_POINTS.forEach((tp) => {
        tmaHourlyMap[tp.name] = filtered.filter((p) => {
          const r = routeString(p);
          if (!r.includes(tp.name)) return false;
          if (!p.longitude || !p.latitude) return false;

          const dist = turf.distance([p.longitude, p.latitude], EFRO_COORDS, {
            units: 'nauticalmiles',
          });
          if (!(dist >= 10 && dist <= 420)) return false;
          if (!(p.groundspeed > 10)) return false;
          return true;
        }).length;
      });

      // 5) Top departure airports (top 6 or fewer)
      const topDeps = Object.entries(depCounts)
        .sort((a, b) => b[1] - a[1]) // sort desc by count
        .slice(0, 6) // up to 6
        .map(([code, count]) => ({ code, count }));

      return {
        tmaTotals: tmaTotalsMap,
        tmaHourly: tmaHourlyMap,
        arrivalRouteTotals: arrivalRouteTotalsMap,
        topDepartures: topDeps,
      };
    }, [trafficData]);

  return (
    <div>
      {/* Toggle Button */}
      <button
        className="flex items-center justify-center fixed bottom-[70px] right-[12px] w-11 h-11 p-2 bg-blue-900 border border-gray-700 rounded-full shadow-lg hover:bg-blue-800 transition"
        onClick={toggleRoutesMenu}
      >
        <FaChartBar className={'text-lg text-gray-300'} />
      </button>

      {check && isVisible && (
        <div className="fixed inset-0 flex items-center justify-center z-40 bg-black bg-opacity-50">
          <div className="relative w-[700px] max-w-[90%] max-h-[80vh] overflow-y-auto bg-gradient-to-br from-slate-900 to-gray-950 border border-gray-800 shadow-xl p-6 text-white">
            {/* Close Button */}
            <button
              className="absolute top-3 right-3 text-gray-300 text-lg hover:bg-red-600 font-bold rounded-full bg-red-700 w-7 h-7 flex items-center justify-center p-0"
              onClick={toggleRoutesMenu}
            >
              <FaTimes />
            </button>
            <h3 className="text-xl font-bold mb-4 border-b border-gray-700 pb-2">
              <span className="flex items-center gap-2 text-gray-300">
                <FaChartBar className={'text-green-500'} />
                EFRO Arrival Stats
              </span>
            </h3>

            {/* Arrival Routes */}
            <div className="space-y-6">
              <div>
                <h4 className="text-lg font-semibold text-pink-500 mb-3">
                  TMA Entry Points
                </h4>
                <div className="grid sm:grid-cols-3 grid-cols-1 gap-2">
                  {TMA_ENTRY_POINTS.map((w) => (
                    <div
                      key={w.name}
                      className="bg-gray-600/20 border border-gray-700 text-gray-200 px-3 py-2 text-sm font-medium flex flex-col items-center justify-center min-w-[80px]"
                    >
                      <span className="font-bold text-blue-400">{w.name}</span>
                      <span className="text-base text-gray-300">
                        {tmaTotals[w.name] ?? 0}
                      </span>
                      <span className="text-sm text-gray-400">
                        {tmaHourly[w.name] ?? 0} /h
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-pink-500 mb-3">
                  Arrival Routes
                </h4>
                <div className="grid sm:grid-cols-3 grid-cols-2 gap-2">
                  {ARRIVAL_ROUTES.map((w) => (
                    <div
                      key={w.name}
                      className="bg-gray-600/20 border border-gray-700 text-gray-200 px-3 py-2 text-sm font-medium flex flex-col items-center justify-center min-w-[80px]"
                    >
                      <span className="font-bold text-blue-400">
                        via {w.name}
                      </span>
                      <span className="text-base text-gray-300">
                        {arrivalRouteTotals[w.name] ?? 0}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Top Departures */}
            <div className="mt-8">
              <h4 className="text-lg font-semibold text-pink-500 mb-3">
                Top {topDepartures.length > 0 ? topDepartures.length : 6}{' '}
                Departure Airports to EFRO
              </h4>
              <div className="grid sm:grid-cols-3 grid-cols-2 gap-2">
                {topDepartures.length > 0
                  ? topDepartures.map((d, idx) => (
                      <div
                        key={d.code}
                        className="bg-gray-600/20 border border-gray-700 text-gray-200 px-3 py-2 text-sm font-medium flex flex-col items-center justify-center min-w-[80px]"
                      >
                        <span className="font-bold text-green-500">
                          {d.code}
                        </span>
                        <span className="text-base text-gray-300">
                          {d.count}
                        </span>
                      </div>
                    ))
                  : // If none, show nothing (or placeholders if you prefer)
                    Array.from({ length: 6 }).map((_, idx) => (
                      <div
                        key={idx}
                        className="bg-gray-800 text-gray-200 px-3 py-2 text-sm font-medium border border-gray-600 flex flex-col items-center justify-center min-w-[80px]"
                      >
                        <span className="font-bold text-green-500">-</span>
                        <span className="text-sm text-gray-300">0</span>
                      </div>
                    ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Stats;
